import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE } from '../constants';

// Use same local development detection logic as App.js
const isLocalDevelopment = process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost';

// Client-side image compression using canvas
async function compressImage(file, { maxSize = 1600, quality = 0.75 } = {}) {
  const bitmap = await createImageBitmap(file);
  const { width, height } = bitmap;
  const scale = Math.min(1, maxSize / Math.max(width, height));
  const targetW = Math.round(width * scale);
  const targetH = Math.round(height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  const dataUrl = await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
  return { dataUrl, blob, width: targetW, height: targetH };
}

function todayStr() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// LocalStorage helper for mock logbook data
const getLogbookEntries = () => {
  try {
    const stored = localStorage.getItem('mockLogbookEntries');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveLogbookEntries = (entries) => {
  try {
    localStorage.setItem('mockLogbookEntries', JSON.stringify(entries));
  } catch (error) {
    console.error('Failed to save logbook entries:', error);
  }
};

// Generate unique ID for mock entries
const generateEntryId = () => `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Mock API functions for local development
const mockFetchEntries = async () => {
  await new Promise(resolve => setTimeout(resolve, 300));
  return getLogbookEntries();
};

const mockSaveEntry = async (entryData) => {
  await new Promise(resolve => setTimeout(resolve, 300));
  const entries = getLogbookEntries();
  const newEntry = {
    id: generateEntryId(),
    ...entryData,
    createdAt: new Date().toISOString()
  };
  const updated = [newEntry, ...entries];
  saveLogbookEntries(updated);
  return { success: true, entry: newEntry };
};

const mockUploadImage = async (fileName, dataUrl, contentType) => {
  await new Promise(resolve => setTimeout(resolve, 500));
  // Simulate upload by returning a mock URL
  return {
    url: `http://localhost:3000/mock-images/${Date.now()}_${fileName}`,
    contentType: contentType || 'image/jpeg'
  };
};

const mockDeleteEntry = async (id) => {
  await new Promise(resolve => setTimeout(resolve, 300));
  const entries = getLogbookEntries();
  const updated = entries.filter(e => e.id !== id);
  saveLogbookEntries(updated);
  return { success: true };
};

const mockUpdateEntry = async (id, updateData) => {
  await new Promise(resolve => setTimeout(resolve, 300));
  const entries = getLogbookEntries();
  const updated = entries.map(e => e.id === id ? { ...e, ...updateData } : e);
  saveLogbookEntries(updated);
  return { success: true, entry: updated.find(e => e.id === id) };
};

// API helpers
async function apiJson(path, opts = {}) {
  // Use mock API in local development
  if (isLocalDevelopment) {
    if (path === '/logbook' && (!opts.method || opts.method === 'GET')) {
      return await mockFetchEntries();
    }
    if (path === '/logbook' && opts.method === 'POST') {
      return await mockSaveEntry(JSON.parse(opts.body));
    }
    if (path.startsWith('/logbook/') && opts.method === 'PUT') {
      const entryId = path.replace('/logbook/', '');
      const updateData = JSON.parse(opts.body);
      return await mockUpdateEntry(entryId, updateData);
    }
    if (path === '/logbook/upload' && opts.method === 'POST') {
      const { fileName, dataUrl, contentType } = JSON.parse(opts.body);
      return await mockUploadImage(fileName, dataUrl, contentType);
    }
  }

  // Real API calls for production
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  const ct = res.headers.get('content-type') || '';
  if (!res.ok) {
    // If missing in preview, gracefully fallback to mock behaviors
    if (res.status === 404) {
      if ((!opts.method || opts.method === 'GET') && path === '/logbook') {
        console.warn('GET /logbook returned 404 – treating as empty list (preview without functions).');
        return [];
      }
      if (opts.method === 'POST' && path === '/logbook') {
        console.warn('POST /logbook returned 404 – saving entry locally (preview without functions).');
        try {
          return await mockSaveEntry(JSON.parse(opts.body || '{}'));
        } catch {
          return await mockSaveEntry({ date: todayStr(), title: 'Okänt', text: '', images: [] });
        }
      }
      if (opts.method === 'POST' && path === '/logbook/upload') {
        console.warn('POST /logbook/upload returned 404 – mocking upload locally (preview without functions).');
        try {
          const { fileName, dataUrl, contentType } = JSON.parse(opts.body || '{}');
          return await mockUploadImage(fileName || 'bild.jpg', dataUrl || '', contentType || 'image/jpeg');
        } catch {
          return await mockUploadImage('bild.jpg', '', 'image/jpeg');
        }
      }
    }
    throw new Error(`HTTP ${res.status}`);
  }
  if (!ct.includes('application/json')) {
    // Likely intercepted by Cloudflare Access; fallback to mock behaviors to keep UI usable
    console.warn('Non-JSON response for', path, '- falling back to local mock behavior in Logbook');
    if (!opts.method || opts.method === 'GET') {
      return await mockFetchEntries();
    }
    if (opts.method === 'POST' && path === '/logbook') {
      try {
        return await mockSaveEntry(JSON.parse(opts.body || '{}'));
      } catch {
        return await mockSaveEntry({ date: todayStr(), title: 'Okänt', text: '', images: [] });
      }
    }
    if (opts.method === 'POST' && path === '/logbook/upload') {
      try {
        const { fileName, dataUrl, contentType } = JSON.parse(opts.body || '{}');
        return await mockUploadImage(fileName || 'bild.jpg', dataUrl || '', contentType || 'image/jpeg');
      } catch {
        return await mockUploadImage('bild.jpg', '', 'image/jpeg');
      }
    }
    if (opts.method === 'PUT' && path.startsWith('/logbook/')) {
      const entryId = path.replace('/logbook/', '');
      try {
        const updateData = JSON.parse(opts.body || '{}');
        return await mockUpdateEntry(entryId, updateData);
      } catch {
        return await mockUpdateEntry(entryId, {});
      }
    }
  }
  return res.json();
}

const Logbook = () => {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(todayStr());
  const [text, setText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [images, setImages] = useState([]); // {file, preview, compressed}
  const [entries, setEntries] = useState([]);
  const [undoInfo, setUndoInfo] = useState(null); // { entry, index }
  const undoTimer = useRef(null);
  const [undoSeconds, setUndoSeconds] = useState(0);
  const undoInterval = useRef(null);
  const [lightbox, setLightbox] = useState(null); // { images: [url], index: number }
  const [warning, setWarning] = useState(''); // show non-blocking warnings to user
  const [editingEntry, setEditingEntry] = useState(null); // Entry being edited
  const [editTitle, setEditTitle] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editText, setEditText] = useState('');

  const fileInputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await apiJson('/logbook');
        if (alive && Array.isArray(data)) setEntries(data);
      } catch (e) {
        console.error('Kunde inte hämta loggbok:', e);
      }
    })();
    return () => { alive = false; };
  }, []);

  const saveEntries = (next) => {
    setEntries(next);
  };

  const onSelectFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const previews = await Promise.all(files.map(async (file) => {
      const preview = await new Promise((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.readAsDataURL(file);
      });
      return { file, preview };
    }));
    setImages((prev) => [...prev, ...previews]);
  };

  const removeImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!title.trim() && !text.trim() && images.length === 0) return;
    setIsSaving(true);
    try {
      // Compress all images
      const compressed = await Promise.all(images.map(async (it) => {
        const result = await compressImage(it.file, { maxSize: 1600, quality: 0.75 });
        return {
          name: it.file.name,
          type: 'image/jpeg',
          size: result.blob?.size || null,
          width: result.width,
          height: result.height,
          dataUrl: result.dataUrl,
        };
      }));

      // Upload each image to R2 and collect URLs
      const uploaded = [];
      let failedUploads = 0;
      for (const img of compressed) {
        try {
          const up = await apiJson('/logbook/upload', {
            method: 'POST',
            body: JSON.stringify({
              fileName: img.name,
              dataUrl: img.dataUrl,
              contentType: img.type,
            }),
          });
          if (up && up.url) {
            uploaded.push({
              url: up.url,
              width: img.width,
              height: img.height,
              contentType: up.contentType || img.type,
              name: img.name,
            });
          }
        } catch (e) {
          console.error('Uppladdning misslyckades:', e);
          failedUploads += 1;
        }
      }

      // Persist entry metadata in D1
      const res = await apiJson('/logbook', {
        method: 'POST',
        body: JSON.stringify({
          date,
          title: title.trim(),
          text: text.trim(),
          images: uploaded,
        }),
      });
      if (res && res.success && res.entry) {
        saveEntries([res.entry, ...entries]);
      }

      // Reset form
      setTitle('');
      setText('');
      setImages([]);

      // Show non-blocking warning if some uploads failed
      if (failedUploads > 0) {
        const plural = failedUploads > 1 ? 'bilder' : 'bild';
        setWarning(`Kunde inte ladda upp ${failedUploads} ${plural}. Inlägget sparades utan dessa.`);
        // auto-hide after 8s
        setTimeout(() => setWarning(''), 8000);
      } else {
        setWarning('');
      }
    } finally {
      setIsSaving(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith('image/'));
    if (!files.length) return;
    const previews = await Promise.all(files.map(async (file) => {
      const preview = await new Promise((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.readAsDataURL(file);
      });
      return { file, preview };
    }));
    setImages((prev) => [...prev, ...previews]);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const pendingDeleteTimer = useRef({});

  const deleteEntry = (id) => {
    if (!id) return;
    const ok = window.confirm('Ta bort inlägget? Detta går att ångra i 10 sek.');
    if (!ok) return;
    // Clear any existing undo timer
    if (undoTimer.current) {
      clearTimeout(undoTimer.current);
      undoTimer.current = null;
      setUndoInfo(null);
    }
    if (undoInterval.current) {
      clearInterval(undoInterval.current);
      undoInterval.current = null;
    }
    const index = entries.findIndex(en => en.id === id);
    if (index === -1) return;
    const entry = entries[index];
    const next = entries.filter((_, i) => i !== index);
    saveEntries(next);
    setUndoInfo({ entry, index });
    // Start 10s undo window
    undoTimer.current = setTimeout(async () => {
      setUndoInfo(null);
      undoTimer.current = null;
      // After undo window, delete on server (or mock in local development)
      try {
        if (isLocalDevelopment) {
          await mockDeleteEntry(id);
        } else {
          await fetch(`${API_BASE}/logbook/${encodeURIComponent(id)}`, { method: 'DELETE' });
        }
      } catch (e) {
        console.error('Delete misslyckades:', e);
      }
    }, 10000);
    setUndoSeconds(10);
    undoInterval.current = setInterval(() => {
      setUndoSeconds((prev) => {
        if (prev <= 1) {
          if (undoInterval.current) {
            clearInterval(undoInterval.current);
            undoInterval.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleUndo = () => {
    if (!undoInfo) return;
    if (undoTimer.current) {
      clearTimeout(undoTimer.current);
      undoTimer.current = null;
    }
    if (undoInterval.current) {
      clearInterval(undoInterval.current);
      undoInterval.current = null;
    }
    const restored = [...entries];
    const insertAt = Math.min(Math.max(undoInfo.index, 0), restored.length);
    restored.splice(insertAt, 0, undoInfo.entry);
    saveEntries(restored);
    setUndoInfo(null);
    setUndoSeconds(0);
  };

  useEffect(() => () => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    if (undoInterval.current) clearInterval(undoInterval.current);
  }, []);

  // Edit functions
  const startEdit = (entry) => {
    setEditingEntry(entry);
    setEditTitle(entry.title || '');
    setEditDate(entry.date || todayStr());
    setEditText(entry.text || '');
  };

  const cancelEdit = () => {
    setEditingEntry(null);
    setEditTitle('');
    setEditDate('');
    setEditText('');
  };

  const saveEdit = async () => {
    if (!editingEntry) return;
    setIsSaving(true);
    try {
      const updateData = {
        title: editTitle.trim(),
        date: editDate,
        text: editText.trim()
      };

      const res = await apiJson(`/logbook/${editingEntry.id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });

      if (res && res.success && res.entry) {
        // Update the entry in the list
        const updated = entries.map(e => e.id === editingEntry.id ? res.entry : e);
        saveEntries(updated);
        cancelEdit();
        setWarning('Inlägg redigerat!');
        setTimeout(() => setWarning(''), 3000);
      }
    } catch (error) {
      console.error('Redigering misslyckades:', error);
      setWarning('Kunde inte redigera inlägget');
      setTimeout(() => setWarning(''), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const openLightbox = (images, index) => {
    if (!Array.isArray(images) || images.length === 0) return;
    const urls = images.map(img => img.url || img.dataUrl || img);
    setLightbox({ images: urls, index: Math.max(0, Math.min(index || 0, urls.length - 1)) });
    // Prevent body scroll optionally
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightbox(null);
    document.body.style.overflow = '';
  };

  const nextLightbox = () => {
    setLightbox(lb => lb ? { ...lb, index: (lb.index + 1) % lb.images.length } : lb);
  };

  const prevLightbox = () => {
    setLightbox(lb => lb ? { ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length } : lb);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!lightbox) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextLightbox();
      if (e.key === 'ArrowLeft') prevLightbox();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  return (
    <div className="logbook">
      <h3>Loggbok</h3>
      <p className="info">Dokumentera vad som blivit gjort i stugan. Lägg gärna till bilder – de komprimeras automatiskt.</p>

      {warning && (
        <div className="snackbar warning" role="status" aria-live="polite" style={{ background: '#fff8e1', border: '1px solid #ffecb3', color: '#7a5d00', padding: '8px 12px', borderRadius: 6, margin: '8px 0' }}>
          {warning}
        </div>
      )}

      <div className="logbook-form">
        <div className="form-row row-date">
          <label>Datum</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="form-row row-title">
          <label>Titel</label>
          <input type="text" placeholder="Kort titel" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="form-row row-desc">
          <label>Beskrivning</label>
          <textarea rows={3} placeholder="Beskriv vad som gjorts" value={text} onChange={(e) => setText(e.target.value)} />
        </div>

        <div className="form-row row-images">
          <label>Bilder</label>
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={onSelectFiles} />
          <div className="dropzone" onDrop={onDrop} onDragOver={onDragOver}>
            Släpp bilder här för att ladda upp
          </div>
          {images.length > 0 && (
            <div className="preview-grid">
              {images.map((img, idx) => (
                <div key={idx} className="preview-item">
                  <img src={img.preview} alt="preview" />
                  <button type="button" className="small danger" onClick={() => removeImage(idx)}>Ta bort</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="actions">
          <button className="primary" onClick={handleSave} disabled={isSaving}>{isSaving ? 'Sparar…' : 'Spara inlägg'}</button>
        </div>
      </div>

      <div className="logbook-list">
        {entries.length === 0 ? (
          <p className="info">Inga inlägg ännu.</p>
        ) : (
          entries.map((e) => {
            if (editingEntry && editingEntry.id === e.id) {
              return (
                <div key={e.id} className="logbook-entry editing">
                  <div className="logbook-form edit-form">
                    <div className="form-row row-date">
                      <label>Datum</label>
                      <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
                    </div>
                    <div className="form-row row-title">
                      <label>Titel</label>
                      <input type="text" placeholder="Kort titel" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                    </div>
                    <div className="form-row row-desc">
                      <label>Beskrivning</label>
                      <textarea rows={3} placeholder="Beskriv vad som gjorts" value={editText} onChange={(e) => setEditText(e.target.value)} />
                    </div>
                    <div className="actions">
                      <button className="primary" onClick={saveEdit} disabled={isSaving}>
                        {isSaving ? 'Sparar…' : 'Spara ändringar'}
                      </button>
                      <button className="secondary" onClick={cancelEdit} disabled={isSaving}>
                        Avbryt
                      </button>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div key={e.id} className="logbook-entry">
                <div className="entry-header">
                  <div className="entry-title">{e.title || '(utan titel)'} </div>
                  <div className="entry-date">{e.date}</div>
                  <button type="button" className="small" onClick={() => startEdit(e)}>Redigera</button>
                  <button type="button" className="small danger" onClick={() => deleteEntry(e.id)}>Ta bort</button>
                </div>
                {e.text && <div className="entry-text">{e.text}</div>}
                {Array.isArray(e.images) && e.images.length > 0 && (
                  <div className="gallery">
                    {e.images.map((img, i) => (
                      <button
                        type="button"
                        key={i}
                        className="thumb"
                        onClick={() => openLightbox(e.images, i)}
                        aria-label="Visa större bild"
                      >
                        <img src={img.url || img.dataUrl} alt={`img-${i}`} loading="lazy" width={img.width} height={img.height} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {undoInfo && (
        <div className="snackbar" role="status" aria-live="polite">
          <div className="snackbar-message">Inlägg borttaget</div>
          <button type="button" className="snackbar-action" onClick={handleUndo}>Ångra ({undoSeconds})</button>
        </div>
      )}

      {lightbox && (
        <div className="lightbox" onClick={closeLightbox} role="dialog" aria-modal="true">
          <div className="lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Stäng">×</button>
            {lightbox.images.length > 1 && (
              <button className="lightbox-prev" onClick={prevLightbox} aria-label="Föregående">‹</button>
            )}
            <img className="lightbox-img" src={lightbox.images[lightbox.index]} alt="förstoring" />
            {lightbox.images.length > 1 && (
              <button className="lightbox-next" onClick={nextLightbox} aria-label="Nästa">›</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Logbook;
